import { useQuery } from '@tanstack/react-query'
import { configApi } from '../lib/api'
import { useCommitteeScope } from './useCommitteeScope'
import type { AvailableDistrictsResponse } from '../types/config'

/**
 * Districts (state senate/house, congressional, etc.) available to the caller within a
 * given state, scoped by the caller's grant (and optionally an acting committee). Disabled
 * until a state is chosen. See vault note "Customer Locality Scoping — State & District
 * Data Access".
 *
 * The active committee scope folds into the key (not the request) so a switch of the
 * parked acting committee forces a refetch even when no explicit actingCommitteeId is
 * passed -- the axios interceptor already appends committeeScope on every request.
 */
export function useAvailableDistricts(state?: string, actingCommitteeId?: string, includeOptions = false) {
  const { activeCommitteeId } = useCommitteeScope()
  return useQuery<AvailableDistrictsResponse>({
    // includeOptions is IN the key: without it a prior options-free response would be served
    // to a caller that needs the option blocks.
    queryKey: ['config', 'available-districts', state, actingCommitteeId ?? activeCommitteeId ?? null, includeOptions],
    enabled: !!state,
    queryFn: () => configApi.getAvailableDistricts(state!, actingCommitteeId, includeOptions).then((r) => r.data),
    staleTime: Infinity,
  })
}
